import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Select, Toast, Typography } from '@douyinfe/semi-ui';
import { IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';
import SubjectSelector from '../../components/SubjectSelector';
import LessonSelector from '../../components/LessonSelector';

const LessonOrders = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [searchSubjectUid, setSearchSubjectUid] = useState('');
    const [searchLessonUid, setSearchLessonUid] = useState('');
    const [searchStaffUid, setSearchStaffUid] = useState('');

    const statusMap = {
        0: { text: '未付款', type: 'warning' },
        1: { text: '已付款', type: 'success' }
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/lesson/list_lesson_order/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                    subject_uid: searchSubjectUid,
                    lesson_uid: searchLessonUid,
                    staff_uid: searchStaffUid
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // 从URL获取参数
        const searchParams = new URLSearchParams(window.location.search);
        const subjectUid = searchParams.get('subject_uid');
        const lessonUid = searchParams.get('lesson_uid');

        if (subjectUid) {
            setSearchSubjectUid(subjectUid);
        }
        if (lessonUid) {
            setSearchLessonUid(lessonUid);
        }

        fetchList();
    }, []);

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const formatPrice = (price) => {
        if (typeof price !== 'number') return '-';
        return (price / 100).toFixed(2);
    };

    const columns = [
        {
            title: '订单信息',
            dataIndex: 'uid',
            width: 300,
            render: (text, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{record.title}</div>
                    <div style={{ color: 'var(--semi-color-text-2)' }}>专题: {record.subject?.name || '-'}</div>
                    <div style={{ color: 'var(--semi-color-text-2)' }}>课程: {record.lesson?.name || '-'}</div>
                </div>
            )
        },
        {
            title: '用户信息',
            dataIndex: 'su_uid',
            width: 200,
            render: (text, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div>用户: {record.su?.name || '-'}</div>
                    <div style={{ color: 'var(--semi-color-text-2)' }}>推荐人: {record.from_su?.name || '-'}</div>
                </div>
            )
        },
        {
            title: '支付信息',
            dataIndex: 'price',
            width: 150,
            render: (text) => (
                <Typography.Text strong>¥{formatPrice(text)}</Typography.Text>
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        }
    ];

    return (
        <div className={styles.lessonOrdersContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <div style={{ width: 200 }}>
                        <SubjectSelector
                            value={searchSubjectUid}
                            onChange={setSearchSubjectUid}
                        />
                    </div>
                    <div style={{ width: 200 }}>
                        <LessonSelector
                            value={searchLessonUid}
                            onChange={setSearchLessonUid}
                        />
                    </div>
                    <Select
                        placeholder="支付状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
            />
        </div>
    );
};

export default LessonOrders;